import {
	llmQueryKeys,
	useBodhiConversations,
	useBodhiMessages,
	useDeleteBodhiConversation,
	useStreamBodhiMessage,
} from "@bodhistreams/queries";
import { useEffect, useMemo, useRef, useState } from "react";
import { IconClose, IconSend, IconSparkles, IconTrash } from "./Icons";
import Message from "./Message";

const addMessageOnce = (messages, message) => {
	if (!message) return messages;
	if (messages.some((item) => item.id === message.id)) return messages;
	return [...messages, message];
};

const appendToolStatus = (toolCalls, nextToolCall) => {
	const existingIndex = toolCalls.findIndex(
		(toolCall) => toolCall.id === nextToolCall.id,
	);
	if (existingIndex === -1) return [...toolCalls, nextToolCall];
	return toolCalls.map((toolCall, index) =>
		index === existingIndex ? { ...toolCall, ...nextToolCall } : toolCall,
	);
};

const createStreamingAssistant = (updates = {}) => ({
	id: "streaming-assistant",
	role: "assistant",
	content: "",
	toolCalls: [],
	isStreaming: true,
	...updates,
});

const Panel = ({ onClose, bottomOffset = 96, rightOffset = 24 }) => {
	const [activeConversationId, setActiveConversationId] = useState(null);
	const [draft, setDraft] = useState("");
	const [pendingMessage, setPendingMessage] = useState(null);
	const [streamingAssistant, setStreamingAssistant] = useState(null);
	const [isStreaming, setIsStreaming] = useState(false);
	const [errorToast, setErrorToast] = useState(null);
	const [confirmClear, setConfirmClear] = useState(false);
	const scrollRef = useRef(null);
	const textareaRef = useRef(null);
	const streamingAssistantRef = useRef(null);

	useEffect(() => {
		streamingAssistantRef.current = streamingAssistant;
	}, [streamingAssistant]);

	const conversationsQuery = useBodhiConversations(true);
	const messagesQuery = useBodhiMessages(activeConversationId, true);
	const { streamMessage, queryClient } = useStreamBodhiMessage();
	const deleteConversation = useDeleteBodhiConversation();

	const conversations = conversationsQuery.data || [];
	const messages = messagesQuery.data || [];

	const visibleMessages = useMemo(() => {
		let next = pendingMessage ? [...messages, pendingMessage] : [...messages];
		if (streamingAssistant) next = [...next, streamingAssistant];
		return next;
	}, [messages, pendingMessage, streamingAssistant]);

	const scrollKey = `${activeConversationId || "new"}:${visibleMessages.length}:${streamingAssistant?.content?.length || 0}:${streamingAssistant?.toolCalls?.length || 0}`;

	useEffect(() => {
		if (!activeConversationId && conversations.length > 0) {
			setActiveConversationId(conversations[0].id);
		}
	}, [activeConversationId, conversations]);

	useEffect(() => {
		const node = scrollRef.current;
		if (node && scrollKey) {
			node.scrollTop = node.scrollHeight;
		}
	}, [scrollKey]);

	useEffect(() => {
		if (!errorToast) return undefined;
		const timer = setTimeout(() => setErrorToast(null), 4000);
		return () => clearTimeout(timer);
	}, [errorToast]);

	useEffect(() => {
		const el = textareaRef.current;
		if (!el) return;
		el.style.height = "auto";
		el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
	}, [draft]);

	const sendMessage = async () => {
		const content = draft.trim();
		if (!content || isStreaming) return;

		setDraft("");
		setIsStreaming(true);
		setPendingMessage({ id: "pending", role: "user", content });
		setStreamingAssistant(createStreamingAssistant());

		let streamConversationId = activeConversationId;

		try {
			await streamMessage({
				conversationId: activeConversationId,
				message: content,
				onEvent: (event) => {
					if (event.type === "started") {
						streamConversationId = event.conversation.id;
						setActiveConversationId(event.conversation.id);
						setPendingMessage(null);
						queryClient.setQueryData(
							llmQueryKeys.messages(event.conversation.id),
							(oldMessages = []) =>
								addMessageOnce(oldMessages, event.user_message),
						);
					}
					if (event.type === "text_delta") {
						setStreamingAssistant((current) =>
							createStreamingAssistant({
								...current,
								content: `${current?.content || ""}${event.delta}`,
							}),
						);
					}
					if (event.type === "tool_call_start") {
						setStreamingAssistant((current) =>
							createStreamingAssistant({
								...current,
								toolCalls: appendToolStatus(current?.toolCalls || [], {
									id: event.tool_call_id,
									name: event.name,
									status: "running",
								}),
							}),
						);
					}
					if (event.type === "tool_call_result") {
						setStreamingAssistant((current) =>
							createStreamingAssistant({
								...current,
								toolCalls: appendToolStatus(current?.toolCalls || [], {
									id: event.tool_call_id,
									name: event.name,
									status: event.ok ? "complete" : "error",
								}),
							}),
						);
					}
					if (event.type === "assistant_message") {
						const accumulatedToolCalls =
							streamingAssistantRef.current?.toolCalls || [];
						const persistedMessage =
							accumulatedToolCalls.length > 0
								? { ...event.message, toolCalls: accumulatedToolCalls }
								: event.message;
						queryClient.setQueryData(
							llmQueryKeys.messages(streamConversationId),
							(oldMessages = []) =>
								addMessageOnce(oldMessages, persistedMessage),
						);
						setStreamingAssistant(null);
						setIsStreaming(false);
					}
					if (event.type === "final") {
						queryClient.invalidateQueries({
							queryKey: llmQueryKeys.conversations(),
						});
					}
				},
			});
		} catch (error) {
			setDraft(content);
			setPendingMessage(null);
			setStreamingAssistant(null);
			setErrorToast(error?.message || "Bodhi could not reply right now.");
		} finally {
			setIsStreaming(false);
		}
	};

	const canClear =
		Boolean(activeConversationId) &&
		!isStreaming &&
		!deleteConversation.isPending;

	const clearChat = async () => {
		if (!canClear) return;
		setConfirmClear(false);
		try {
			await deleteConversation.mutateAsync(activeConversationId);
			setActiveConversationId(null);
			setPendingMessage(null);
			setStreamingAssistant(null);
		} catch (_error) {
			setErrorToast("Could not clear the chat.");
		}
	};

	const handleKeyDown = (event) => {
		if (event.key === "Enter" && !event.shiftKey) {
			event.preventDefault();
			sendMessage();
		}
	};

	const isLoading = messagesQuery.isLoading || conversationsQuery.isLoading;
	const canSend = Boolean(draft.trim()) && !isStreaming;

	return (
		<>
			<div
				className="bodhi-float__panel"
				role="dialog"
				aria-label="Bodhi chat"
				style={{
					right: rightOffset,
					bottom: bottomOffset,
					"--bf-panel-bottom": `${bottomOffset}px`,
					"--bf-panel-right": `${rightOffset}px`,
				}}
			>
				<header className="bodhi-float__header">
					<div className="bodhi-float__header-info">
						<span className="bodhi-float__avatar">
							<IconSparkles />
							<span className="bodhi-float__avatar-status" />
						</span>
						<div className="bodhi-float__title">
							<h3 className="bodhi-float__title-name">Bodhi</h3>
							<p className="bodhi-float__title-sub">BodhiStreams guide</p>
						</div>
					</div>
					<div className="bodhi-float__header-actions">
						{(activeConversationId || deleteConversation.isPending) && (
							<button
								type="button"
								className="bodhi-float__icon-btn"
								aria-label="Clear chat"
								title="Clear chat"
								onClick={() => setConfirmClear(true)}
								disabled={!canClear}
							>
								{deleteConversation.isPending ? (
									<span className="bodhi-float__icon-spinner" />
								) : (
									<IconTrash />
								)}
							</button>
						)}
						<button
							type="button"
							className="bodhi-float__icon-btn"
							aria-label="Close Bodhi chat"
							onClick={onClose}
						>
							<IconClose />
						</button>
					</div>
				</header>

				{confirmClear && (
					<div className="bodhi-float__confirm" role="alertdialog">
						<p className="bodhi-float__confirm-text">
							<strong>Clear chat?</strong> This deletes the conversation and its
							history. It can&apos;t be undone.
						</p>
						<div className="bodhi-float__confirm-actions">
							<button
								type="button"
								className="bodhi-float__confirm-btn"
								onClick={() => setConfirmClear(false)}
							>
								Cancel
							</button>
							<button
								type="button"
								className="bodhi-float__confirm-btn bodhi-float__confirm-btn--danger"
								onClick={clearChat}
							>
								Clear
							</button>
						</div>
					</div>
				)}

				<div ref={scrollRef} className="bodhi-float__scroll">
					{isLoading ? (
						<div className="bodhi-float__center">
							<div className="bodhi-float__spinner" />
						</div>
					) : visibleMessages.length > 0 ? (
						<div className="bodhi-float__messages">
							{visibleMessages.map((chatMessage) => (
								<Message key={chatMessage.id} message={chatMessage} />
							))}
						</div>
					) : (
						<div className="bodhi-float__center">
							<div className="bodhi-float__empty-orb">
								<IconSparkles />
							</div>
							<h4 className="bodhi-float__empty-title">Hi, I&apos;m Bodhi.</h4>
							<p className="bodhi-float__empty-sub">
								Ask me anything about your music, playlists, or what to listen
								to next.
							</p>
						</div>
					)}
				</div>

				<div className="bodhi-float__composer">
					<div className="bodhi-float__input-shell">
						<textarea
							ref={textareaRef}
							className="bodhi-float__textarea"
							placeholder="Message Bodhi…"
							value={draft}
							onChange={(event) => setDraft(event.target.value)}
							onKeyDown={handleKeyDown}
							disabled={isStreaming}
							rows={1}
						/>
						<button
							type="button"
							className="bodhi-float__send"
							aria-label="Send message to Bodhi"
							onClick={sendMessage}
							disabled={!canSend}
						>
							{isStreaming ? (
								<span className="bodhi-float__send-spinner" />
							) : (
								<IconSend />
							)}
						</button>
					</div>
				</div>
			</div>

			{errorToast && <div className="bodhi-float__toast">{errorToast}</div>}
		</>
	);
};

export default Panel;
